import { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { env } from '@/shared/config/env';
import {
  createScreenistaMarkerIcon,
  getMarkerIconConfig,
  loadGoogleMapsScript,
} from '@/shared/lib/maps/googleMaps';

type MinimalPlaceMapProps = {
  lat: number;
  lng: number;
  /** Radius in kilometers — drawn as a circle and used to fit bounds. */
  radiusKm?: number;
  title?: string;
  className?: string;
  /** Compact sheet height vs default preview. */
  height?: number;
};

/**
 * Lightweight Google Map preview: center pin + optional search-radius circle.
 * Reuses the singleton Maps loader (no extra script cost).
 */
export function MinimalPlaceMap({
  lat,
  lng,
  radiusKm = 5,
  title,
  className = '',
  height = 148,
}: MinimalPlaceMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const apiKey = env.GOOGLE_LOCATION_KEY;

  useEffect(() => {
    if (!hasCoords || !apiKey || !mapRef.current) {
      setStatus(apiKey ? 'loading' : 'error');
      return;
    }

    let cancelled = false;

    void loadGoogleMapsScript(apiKey)
      .then(async (googleMaps) => {
        if (cancelled || !mapRef.current) return;

        const position = { lat, lng };
        const radiusMeters = Math.max(100, (radiusKm || 5) * 1000);

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new googleMaps.Map(mapRef.current, {
            center: position,
            zoom: 13,
            mapTypeId: 'roadmap',
            disableDefaultUI: true,
            clickableIcons: false,
            gestureHandling: 'cooperative',
            keyboardShortcuts: false,
            styles: [
              {
                featureType: 'poi',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'transit',
                stylers: [{ visibility: 'off' }],
              },
            ],
          });
        } else {
          mapInstanceRef.current.setCenter(position);
        }

        const map = mapInstanceRef.current;

        let iconUrl: string | null = null;
        try {
          iconUrl = await createScreenistaMarkerIcon();
        } catch {
          iconUrl = null;
        }
        if (cancelled) return;

        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }

        markerRef.current = new googleMaps.Marker({
          position,
          map,
          title: title || 'Selected place',
          optimized: true,
          ...(iconUrl
            ? { icon: getMarkerIconConfig(googleMaps, iconUrl) }
            : {}),
        });

        if (circleRef.current) {
          circleRef.current.setMap(null);
          circleRef.current = null;
        }

        circleRef.current = new googleMaps.Circle({
          map,
          center: position,
          radius: radiusMeters,
          fillColor: '#2979FF',
          fillOpacity: 0.12,
          strokeColor: '#2979FF',
          strokeOpacity: 0.55,
          strokeWeight: 1.5,
          clickable: false,
        });

        const bounds = circleRef.current.getBounds();
        if (bounds) {
          map.fitBounds(bounds, 28);
        }

        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [hasCoords, apiKey, lat, lng, radiusKm, title]);

  useEffect(() => {
    return () => {
      markerRef.current?.setMap(null);
      circleRef.current?.setMap(null);
      markerRef.current = null;
      circleRef.current = null;
      mapInstanceRef.current = null;
    };
  }, []);

  if (!hasCoords) return null;

  if (!apiKey || status === 'error') {
    return (
      <div
        className={`flex items-center justify-center gap-2 rounded-[14px] border border-dashed border-black/[0.08] bg-[#F7F8FA] text-[11px] text-[var(--dash-muted)] ${className}`}
        style={{ height }}
      >
        <MapPin className="h-3.5 w-3.5" />
        Map preview unavailable
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-[14px] border border-black/[0.06] bg-[#E8EEF5] ${className}`}
      style={{ height }}
    >
      <div ref={mapRef} className="h-full w-full" aria-label="Selected place map" />
      {status === 'loading' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-[#E8EEF5]/80">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--dash-brand)]" />
        </div>
      ) : null}
      <div className="pointer-events-none absolute bottom-2 left-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-[var(--dash-ink)] shadow-sm ring-1 ring-black/[0.06]">
        {radiusKm} km radius
      </div>
    </div>
  );
}
